/**
 * Transforms a concept into a proper query for deeper exploration
 * @param concept - The concept to transform
 * @param parentQuery - The original query that generated this concept
 * @returns A more specific query that dives deeper into the concept
 */
export function conceptToQuery(concept: string, parentQuery: string): string {
  // If the concept is already a question, return it as is
  if (concept.includes('?')) {
    return concept;
  }
  
  // If the concept starts with certain keywords, format accordingly
  const lowerConcept = concept.toLowerCase();
  
  if (lowerConcept.startsWith('the role of') || lowerConcept.startsWith('the impact of')) {
    return `${concept} in the context of ${parentQuery}`;
  }
  
  if (lowerConcept.startsWith('how') || lowerConcept.startsWith('why') || lowerConcept.startsWith('what')) {
    return concept;
  }
  
  // For interpretations or theories
  if (lowerConcept.includes('interpretation') || lowerConcept.includes('theory')) {
    return `What are the different ${concept}?`;
  }
  
  // For comparisons
  if (lowerConcept.includes('comparison') || lowerConcept.includes('difference')) {
    return concept;
  }
  
  // Default: Create a "What is/are" question
  const article = concept.match(/^(a|an|the)\s/i) ? '' : 'the ';
  return `What is ${article}${concept} and how does it relate to ${parentQuery}?`;
}

/**
 * Generates a contextual description for why this concept is worth exploring
 */
export function getConceptDescription(concept: string): string {
  const lowerConcept = concept.toLowerCase();
  
  if (lowerConcept.includes('role') || lowerConcept.includes('function')) {
    return 'Explore the significance and function';
  }
  
  if (lowerConcept.includes('impact') || lowerConcept.includes('influence')) {
    return 'Understand the effects and implications';
  }
  
  if (lowerConcept.includes('theory') || lowerConcept.includes('interpretation')) {
    return 'Dive into different perspectives';
  }
  
  if (lowerConcept.includes('comparison') || lowerConcept.includes('versus')) {
    return 'Compare and contrast key differences';
  }
  
  return 'Learn more about this topic';
}